import CheckIcon from '@mui/icons-material/Check';
import SortIcon from '@mui/icons-material/Sort';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import React, { useState } from 'react';
import {
  MarketplaceSortValue,
  marketplaceSortGroups,
  marketplaceSortLabel,
} from 'marketplace/search/marketplace-sorts';


export interface SortButtonProps {
  sortBy: MarketplaceSortValue;
  onSort: (value: MarketplaceSortValue) => void;
}

export default function SortButton(props: Readonly<SortButtonProps>) {

  const [sortByAnchor, setSortByAnchor] = useState<null | HTMLElement>(null);

  const selectSort = (value: MarketplaceSortValue) => {
    setSortByAnchor(null);
    props.onSort(value);
  };

  // The button carries the active sort rather than a bare "Sort". Without it the only way to learn
  // the results were ordered by billing rate was to recognize the ordering, and a sort selected on
  // an earlier search stayed in force with nothing on screen to say so.
  const activeLabel = marketplaceSortLabel(props.sortBy);

  return (
    <React.Fragment>
      <Button
        size="large"
        color="primary"
        startIcon={<SortIcon />}
        aria-label={`Sort. Currently sorted by ${activeLabel}`}
        onClick={(event) => setSortByAnchor(event.currentTarget)}>
        {`Sort: ${activeLabel}`}
      </Button>
      <Menu
        anchorEl={sortByAnchor}
        open={Boolean(sortByAnchor)}
        onClose={() => setSortByAnchor(null)}
        keepMounted={true}>
        {marketplaceSortGroups.flatMap((group, groupIndex) => [
          ...(groupIndex > 0
            ? [<Divider key={`divider-${group.key}`} sx={{ opacity: 0.6 }} variant="middle" />]
            : []),
          ...group.options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              // One choice out of a set, which is a radio group rather than a list of commands.
              // A plain menuitem has nowhere to carry "this is the active sort": aria-selected is
              // not valid on it, so the checkmark would be visible but unannounced.
              role="menuitemradio"
              aria-checked={option.value === props.sortBy}
              selected={option.value === props.sortBy}
              onClick={() => selectSort(option.value)}>
              <ListItemIcon>
                {option.value === props.sortBy ? <CheckIcon fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText
                primary={option.label}
                secondary={option.appliesTo} />
            </MenuItem>
          )),
        ])}
      </Menu>
    </React.Fragment>
  );
}
